import {
    Alert,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import { StatusResponse } from "@rossum/upcloud-costs-shared";

import { fetchStatus } from "../../api/status-api";
import { AsyncContent } from "../../common/AsyncContent";
import { formatMoney, formatMonth, formatTimestamp } from "../../common/format";
import { useAsync } from "../../common/use-async";

/** Freshness of the billing data and the inventory, and the recent runs. */
export function StatusPage() {
    const { t } = useTranslation();
    const status = useAsync(fetchStatus, []);
    return (
        <Stack spacing={2}>
            <Typography variant="h5" component="h2">
                {t("status.title")}
            </Typography>
            <AsyncContent state={status}>{(data) => <StatusView status={data} />}</AsyncContent>
        </Stack>
    );
}

function StatusView({ status }: { status: StatusResponse }) {
    const { t, i18n } = useTranslation();
    const locale = i18n.language;
    return (
        <Stack spacing={3}>
            <section>
                <Typography variant="h6" component="h3" gutterBottom>
                    {t("status.inventory")}
                </Typography>
                {status.lastInventory === null ? (
                    <Alert severity="warning">{t("status.noInventory")}</Alert>
                ) : (
                    <Typography variant="body2">
                        {t("status.capturedAt")}: {formatTimestamp(status.lastInventory.capturedAt, locale)} ·{" "}
                        {t("status.uploadedAt")}: {formatTimestamp(status.lastInventory.uploadedAt, locale)} ·{" "}
                        {t("status.client")}: {status.lastInventory.clientId} · {t("status.resources")}:{" "}
                        {status.lastInventory.resourceCount}
                    </Typography>
                )}
            </section>
            <section>
                <Typography variant="h6" component="h3" gutterBottom>
                    {t("status.runs")}
                </Typography>
                <TableContainer component={Paper}>
                    <Table size="small" aria-label={t("status.runs")}>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t("status.kind")}</TableCell>
                                <TableCell>{t("status.state")}</TableCell>
                                <TableCell>{t("status.started")}</TableCell>
                                <TableCell>{t("status.finished")}</TableCell>
                                <TableCell align="right">{t("status.resources")}</TableCell>
                                <TableCell>{t("status.errorCode")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {status.runs.map((run) => (
                                <TableRow key={run.collectorRunId}>
                                    <TableCell>{t(`runKind.${run.kind}`)}</TableCell>
                                    <TableCell>{t(`runStatus.${run.status}`)}</TableCell>
                                    <TableCell>{formatTimestamp(run.startedAt, locale)}</TableCell>
                                    <TableCell>
                                        {run.finishedAt === null ? "–" : formatTimestamp(run.finishedAt, locale)}
                                    </TableCell>
                                    <TableCell align="right">{run.resourceCount}</TableCell>
                                    <TableCell>{run.errorCode ?? "–"}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </section>
            <section>
                <Typography variant="h6" component="h3" gutterBottom>
                    {t("status.periods")}
                </Typography>
                <TableContainer component={Paper}>
                    <Table size="small" aria-label={t("status.periods")}>
                        <TableHead>
                            <TableRow>
                                <TableCell>{t("status.month")}</TableCell>
                                <TableCell align="right">{t("status.total")}</TableCell>
                                <TableCell>{t("status.fetchedAt")}</TableCell>
                                <TableCell>{t("status.closed")}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {status.periods.map((period) => (
                                <TableRow key={period.month}>
                                    <TableCell>{formatMonth(period.month, locale)}</TableCell>
                                    <TableCell align="right">
                                        {formatMoney(period.totalAmount, period.currency, locale)}
                                    </TableCell>
                                    <TableCell>{formatTimestamp(period.fetchedAt, locale)}</TableCell>
                                    <TableCell>{period.closed ? t("common.yes") : t("common.no")}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </section>
        </Stack>
    );
}
